'use client';

// ── Exchange-rate provider ────────────────────────────────────────────────
// Resolves the USDC→local-currency exchange-rate table ONCE for the whole app
// and shares it via context.
//
// Why a context for SDK-backed data (a deviation from the three-tier model's
// "SDK hooks at component level" default): the rate table is a single global,
// effectively immutable value — `useGetExchangeRates` is a non-realtime
// `kind: single` query with a 60s staleTime (see sdk.manifest.yml). Before this
// provider, `useFormattedCurrency()` called `useGetExchangeRates` internally,
// and ~1590 money-displaying leaves on the `/markets` feed each spun up their
// own SDK query observer for that one value (measured: 1590 live instances,
// 67 commits/sec). Hoisting the single hook call here collapses that to ONE
// observer; every `useFormattedCurrency()` caller reads the shared context
// instead. SSR still hydrates the query (see `lib/sdk/ssr/shell.ts`), so this
// provider sees the prefetched data on first paint.
//
// The provider issues exactly one `useGetExchangeRates` observer for the whole
// app; the context value object is memoised automatically by the React
// Compiler (no manual `useMemo` — manual memo hooks are banned here), so
// consuming it adds no re-renders beyond an actual rates/loading change.

import { useGetExchangeRates } from '@morfi/backend-sdk-v2/client';
import { type Context, createContext, type ReactNode, use } from 'react';

export interface ExchangeRateContextValue {
  /** USDC→local-currency rates keyed by ISO currency code. `undefined` until loaded. */
  rates: Record<string, number> | undefined;
  /** `true` while the single app-wide rates query is still pending. */
  isLoading: boolean;
}

const FALLBACK: ExchangeRateContextValue = { rates: undefined, isLoading: false };

const ExchangeRateContext: Context<ExchangeRateContextValue> =
  createContext<ExchangeRateContextValue>(FALLBACK);

/**
 * Mount once near the app root (inside `<SDKProvider>`). Issues the single
 * `useGetExchangeRates` call for the entire app.
 */
export function ExchangeRateProvider({ children }: { children: ReactNode }): ReactNode {
  const { data, isPending } = useGetExchangeRates({ fields: ['rates'] });
  const rates = data?.rates as Record<string, number> | undefined;

  // The React Compiler memoises this object construction; consumers re-render
  // only when `rates` or `isPending` actually change.
  const value: ExchangeRateContextValue = { rates, isLoading: isPending };

  return <ExchangeRateContext.Provider value={value}>{children}</ExchangeRateContext.Provider>;
}

/**
 * Read the app-wide exchange-rate table. Returns the shared context value —
 * does NOT create a new SDK query observer.
 */
export function useExchangeRates(): ExchangeRateContextValue {
  return use(ExchangeRateContext);
}
